/* ═══════════════════════════════════════════════════════════════════════════
   Palettes réservées, et le test qui les défend (PLAN.md §4.5).

   Règle : chaque rôle possède ses couleurs, et aucune ne traverse jamais la
   frontière. C'est ce qui permet à un joueur d'apprendre en trente secondes,
   sans qu'on le lui dise, que « rose ou rouge = ça tue » — et de ne jamais
   avoir à se le redemander pendant les quinze heures suivantes.

   La contrainte forte est sur la LUMINANCE, pas sur la teinte : l'axe
   rouge/orange est précisément celui que confondent les daltonismes deutan et
   protan, qui touchent environ 8 % des hommes. La distinction est donc portée
   d'abord par la forme et le contraste, la couleur n'arrivant qu'en renfort.
   ═══════════════════════════════════════════════════════════════════════════ */

/** ⚠️ Interdites partout ailleurs : décor, explosions, HUD, ennemis, interface. */
export const ENEMY_BULLET = ['#FF2A4D', '#FF5FA2', '#B15CFF'] as const;
export const PLAYER_BULLET = ['#3FE8FF', '#FFFFFF'] as const;
/** Tir SECONDAIRE. Vert franc, choisi par élimination plutôt que par goût :
 *  le rouge, le rose et le violet appartiennent au danger et sont interdits
 *  ailleurs ; le cyan est déjà le tir primaire ; l'ambre est celui des
 *  ramassables et l'orange celui des explosions. Le vert est la seule plage
 *  restante, et elle a l'avantage d'être la plus éloignée du cyan sur l'axe
 *  que confondent les daltonismes deutan et protan.
 *
 *  La couleur ne fait pourtant que la moitié du travail : la taille réelle du
 *  projectile porte le reste (voir `View.sync`). */
export const PLAYER_SECONDARY = ['#54FFB0', '#FFFFFF'] as const;
/** Tir des DRONES d'escorte. Un cyan volontairement PLUS PÂLE que le tir
 *  primaire `#3FE8FF` : le drone est un calibre d'appui, pas le canon principal,
 *  et sa balle doit se lire comme plus légère. Comme partout, la teinte n'est
 *  qu'un renfort : l'identité est portée d'abord par la TAILLE (plus petite,
 *  dérivée du rayon réel du projectile) et la forme (voir `View.sync`). Le cyan
 *  reste la couleur du joueur ; l'appui ne franchit jamais vers une autre
 *  famille, il se contente d'en éclaircir la valeur. */
export const PLAYER_DRONE = ['#B8F4FF', '#FFFFFF'] as const;
export const EXPLOSION = ['#FF8A1F', '#FFE04A', '#FFF6E0'] as const;
export const PICKUP = ['#FFC93C'] as const;
export const ENEMY_BODY = ['#3A4A5C', '#4A7FA5', '#6A5FA5'] as const;
export const ENEMY_ACCENT = ['#FF8A1F'] as const;
export const UI = ['#22E0FF', '#FFFFFF', '#FF3BD4'] as const;

/** Anatomie d'un projectile ennemi : trois zones concentriques.
 *
 *  Le contraste clair/sombre est intégré AU PROJECTILE LUI-MÊME, ce qui le rend
 *  lisible sur fond clair comme sur fond sombre sans rien supposer du décor.
 *  Un halo qui dépendrait du fond échouerait exactement là où on en a le plus
 *  besoin : au-dessus d'une explosion. */
export const BULLET_CORE = '#FFFFFF';
export const BULLET_RIM = '#1A0008';
/** Le noyau blanc occupe 45 % du rayon — et c'est aussi, à 85 %, la hitbox
 *  effective. La lecture visuelle est donc littéralement exacte. */
export const BULLET_CORE_RATIO = 0.45;

/* ═══════════════════════════════════════════════════════════════════════════
   L'ATLAS DE PALETTE (PLAN.md §5.3, jalon J13).

   « Pour un jeu néon/low-poly, le meilleur pipeline texture est celui qu'on n'a
   pas » : couleurs de sommet + émissif + UN SEUL atlas partagé. Ici l'atlas est
   une grille 4 × 4 de cellules, générée en mémoire au démarrage — aucun octet
   téléchargé, aucun transcodeur, aucun décodage.

   Deux conséquences qui valent bien plus que les octets économisés :

     1. **Un seul matériau pour toutes les coques.** La couleur d'une facette
        est une COORDONNÉE, pas un matériau. Vingt-deux archétypes partagent
        donc un unique programme de shader — ce qui est exactement la contrainte
        du warm-up (§4.8 : « le catalogue de matériaux est FERMÉ »).
     2. **La palette devient vérifiable.** Toutes les couleurs de surface du jeu
        étant dans ce tableau, un test peut affirmer qu'aucune n'empiète sur la
        palette danger. Une règle qu'on peut exécuter est une règle qui survit.

   ⚠️ Le canal ALPHA ne porte pas de transparence : il porte le MASQUE
   D'ÉMISSION. Les coques sont opaques ; l'alpha échantillonné est donc libre, et
   c'est le seul canal disponible pour dire « cette facette glow » sans payer une
   seconde texture ni un second jeu d'UV. Voir `hullMaterial.ts`.
   ═══════════════════════════════════════════════════════════════════════════ */

export interface AtlasCell {
  readonly name: string;
  readonly hex: string;
  /** 0 = mat, 1 = pleinement émissif. C'est ce qui franchit le seuil de bloom. */
  readonly glow: number;
}

/** ⚠️ L'ORDRE EST UNE CLÉ. Les géométries référencent les cellules par INDICE ;
 *  insérer une couleur au milieu repeindrait silencieusement toute la flotte. */
export const ATLAS_CELLS: readonly AtlasCell[] = [
  // Coques ennemies — le gris-bleu du plan et ses deux dérivés.
  { name: 'hull', hex: '#3A4A5C', glow: 0 },
  { name: 'hullDark', hex: '#232E3B', glow: 0 },
  { name: 'trim', hex: '#56708C', glow: 0 },
  // Accents ORANGE sur les points faibles — jamais rouge, jamais rose.
  { name: 'accent', hex: '#FF8A1F', glow: 0.9 },
  { name: 'accentDim', hex: '#B85E14', glow: 0.22 },
  // Joueur : le cyan lui appartient, il n'apparaît sur rien d'hostile.
  { name: 'playerHull', hex: '#2F6F8F', glow: 0 },
  { name: 'playerTrim', hex: '#7FD9F2', glow: 0.12 },
  { name: 'playerGlow', hex: '#3FE8FF', glow: 1 },
  // Escorte : même famille, volontairement plus sombre et plus froide, pour
  // qu'on ne cherche jamais lequel des neuf appareils on pilote.
  { name: 'droneHull', hex: '#1E5570', glow: 0 },
  { name: 'droneGlow', hex: '#3FE8FF', glow: 0.55 },
  // Boss : plus sombres que la troupe. Une masse, pas un gros ennemi.
  { name: 'bossHull', hex: '#2C3947', glow: 0 },
  { name: 'bossTrim', hex: '#6B8299', glow: 0 },
  // Décor.
  { name: 'decorFar', hex: '#14263A', glow: 0 },
  { name: 'decorMid', hex: '#1B3350', glow: 0 },
  // Ramassables.
  { name: 'pickup', hex: '#FFC93C', glow: 0.85 },
  // Partie détruite : noircie, toujours attachée (PLAN.md §6.4, E12).
  { name: 'dead', hex: '#12161C', glow: 0 },
];

/** Indices nommés, pour que les géométries se lisent. */
export const Cell = {
  Hull: 0,
  HullDark: 1,
  Trim: 2,
  Accent: 3,
  AccentDim: 4,
  PlayerHull: 5,
  PlayerTrim: 6,
  PlayerGlow: 7,
  DroneHull: 8,
  DroneGlow: 9,
  BossHull: 10,
  BossTrim: 11,
  DecorFar: 12,
  DecorMid: 13,
  Pickup: 14,
  Dead: 15,
} as const;

/** Grille 4 × 4, cellules de 16 px → 64 × 64. Le plan demande 64-128 px, et en
 *  dessous de 512 px le transcodeur Basis coûterait plus cher que le gain. */
export const ATLAS_SIDE = 4;
export const ATLAS_CELL_PX = 16;
export const ATLAS_PX = ATLAS_SIDE * ATLAS_CELL_PX;

/** Cellules considérées comme du DÉCOR, soumises au plafond de luminance. */
const DECOR_CELLS = ['decorFar', 'decorMid'];

export function hexToRgb(hex: string): [number, number, number] {
  const h = hex.replace('#', '');
  return [
    parseInt(h.slice(0, 2), 16) / 255,
    parseInt(h.slice(2, 4), 16) / 255,
    parseInt(h.slice(4, 6), 16) / 255,
  ];
}

/** Luminance relative (Rec. 709), en espace linéaire.
 *  C'est la grandeur que l'œil trie en premier, et la seule qui reste valable
 *  en vision daltonienne. */
export function luminance(hex: string): number {
  const [r, g, b] = hexToRgb(hex);
  const lin = (c: number) => (c <= 0.04045 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4);
  return 0.2126 * lin(r) + 0.7152 * lin(g) + 0.0722 * lin(b);
}

/* ── La bande de teintes RÉSERVÉE ─────────────────────────────────────────
   La règle du plan (§4.5) dit « rouge, rose et violet interdits ailleurs ». Une
   liste de trois hexadécimaux ne suffit pas à la faire respecter : #FF3050 n'est
   pas #FF2A4D, et personne ne verra la différence — sauf le joueur, qui prendra
   ce décor pour une balle.

   La règle utile porte donc sur un SECTEUR DE TEINTE, pas sur des valeurs. Les
   trois familles de projectiles occupent 271° (violet), 335° (rose) et 350°
   (rouge) : la bande interdite est l'arc qui les contient, avec sa marge.

   Elle ne s'applique qu'aux surfaces 3D — atlas de coques, décor, sortie des
   LUT. L'interface a le droit d'employer le magenta `#FF3BD4` (§4.5, ligne UI) :
   elle vit dans le DOM, au-dessus du rendu, et jamais dans le terrain. */
export const RESERVED_HUE_MIN = 262;
export const RESERVED_HUE_MAX = 14;
/** En dessous de cette saturation, une teinte de la bande n'est plus lisible
 *  comme « rouge » : un gris légèrement chaud ne trompe personne. */
export const RESERVED_SAT_LIMIT = 0.28;

/** Teinte HSV en degrés [0, 360). */
export function hueDeg(r: number, g: number, b: number): number {
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const d = max - min;
  if (d < 1e-6) return 0;
  let h: number;
  if (max === r) h = ((g - b) / d) % 6;
  else if (max === g) h = (b - r) / d + 2;
  else h = (r - g) / d + 4;
  h *= 60;
  return h < 0 ? h + 360 : h;
}

/** Saturation HSV. */
export function satHsv(r: number, g: number, b: number): number {
  const max = Math.max(r, g, b);
  if (max < 1e-6) return 0;
  return (max - Math.min(r, g, b)) / max;
}

/** `true` si cette couleur empiète sur la palette danger — c'est-à-dire si un
 *  joueur pourrait, une fraction de seconde, la prendre pour un projectile. */
export function inReservedBand(r: number, g: number, b: number): boolean {
  const s = satHsv(r, g, b);
  // Une couleur sombre ou pâle ne concurrence pas une balle : la couche danger
  // est saturée ET claire. Le seuil de valeur évite de condamner un brun.
  if (s <= RESERVED_SAT_LIMIT || Math.max(r, g, b) < 0.25) return false;
  const h = hueDeg(r, g, b);
  return h >= RESERVED_HUE_MIN || h < RESERVED_HUE_MAX;
}

/** Contraste WCAG entre deux couleurs, de 1 (identiques) à 21 (noir/blanc). */
export function contrastRatio(a: string, b: string): number {
  const la = luminance(a);
  const lb = luminance(b);
  const hi = la > lb ? la : lb;
  const lo = la > lb ? lb : la;
  return (hi + 0.05) / (lo + 0.05);
}

export interface PaletteViolation {
  readonly rule: string;
  readonly detail: string;
}

/**
 * Vérifie les règles de palette. Tourne en CI.
 *
 * Le point à retenir quand ce test échoue : **c'est le décor ou l'explosion
 * qu'on modifie, jamais la balle.** La couche danger est l'invariant du
 * système ; tout le reste s'y adapte.
 */
export function checkPalette(): PaletteViolation[] {
  const out: PaletteViolation[] = [];

  // 1. Aucune couleur de projectile ennemi ne doit apparaître ailleurs.
  const reserved = new Set<string>(ENEMY_BULLET.map((c) => c.toUpperCase()));
  const others = [
    ...PLAYER_BULLET,
    ...PLAYER_SECONDARY,
    ...PLAYER_DRONE,
    ...EXPLOSION,
    ...PICKUP,
    ...ENEMY_BODY,
    ...ENEMY_ACCENT,
    ...UI,
  ];
  for (const c of others) {
    if (reserved.has(c.toUpperCase())) {
      out.push({ rule: 'palette danger réservée', detail: `${c} employée hors des projectiles ennemis` });
    }
  }

  // 2. Le noyau et le contour doivent trancher franchement l'un sur l'autre :
  //    c'est ce contraste interne qui rend la balle lisible sur n'importe quel
  //    fond, y compris sur une explosion.
  const coreRim = contrastRatio(BULLET_CORE, BULLET_RIM);
  if (coreRim < 12) {
    out.push({ rule: 'contraste interne du projectile', detail: `${coreRim.toFixed(1)}:1 (min 12:1)` });
  }

  // 3. Chaque famille de projectile ennemi doit trancher sur le noyau blanc,
  //    sinon le corps coloré se fond dedans et la balle perd sa forme.
  for (const c of ENEMY_BULLET) {
    const r = contrastRatio(c, BULLET_CORE);
    if (r < 1.8) {
      out.push({ rule: 'corps vs noyau', detail: `${c} : ${r.toFixed(2)}:1 (min 1.8:1)` });
    }
  }

  // 4. Les explosions ne doivent jamais employer de rouge saturé : elles
  //    occupent une grande surface et entreraient en concurrence directe avec
  //    la couche danger au pire moment — quand tout explose.
  for (const c of EXPLOSION) {
    const [r, g, b] = hexToRgb(c);
    if (r > 0.75 && g < 0.35 && b < 0.35) {
      out.push({ rule: 'explosions sans rouge saturé', detail: `${c} trop proche du rouge danger` });
    }
  }

  // 5. Les projectiles joueur doivent trancher sur les projectiles ennemis —
  //    confondre son propre tir avec ce qui vous tue est la faute de lisibilité
  //    la plus grave possible.
  for (const p of PLAYER_BULLET) {
    for (const e of ENEMY_BULLET) {
      const [pr, pg, pb] = hexToRgb(p);
      const [er, eg, eb] = hexToRgb(e);
      const dist = Math.abs(pr - er) + Math.abs(pg - eg) + Math.abs(pb - eb);
      if (dist < 0.5) {
        out.push({ rule: 'tir joueur vs tir ennemi', detail: `${p} trop proche de ${e}` });
      }
    }
  }

  // 6. L'ATLAS DE PALETTE — toutes les surfaces 3D du jeu passent par lui.
  //    C'est la vérification qui a le plus de valeur : elle ne porte plus sur
  //    une poignée de constantes documentaires mais sur ce qui est réellement
  //    dessiné, et elle attrape la teinte APPROCHANTE, pas seulement la teinte
  //    identique.
  const seen = new Set<string>();
  for (const cell of ATLAS_CELLS) {
    const [r, g, b] = hexToRgb(cell.hex);
    if (inReservedBand(r, g, b)) {
      out.push({
        rule: 'bande de teintes réservée',
        detail: `cellule « ${cell.name} » (${cell.hex}, teinte ${hueDeg(r, g, b).toFixed(0)}°) empiète sur la palette danger`,
      });
    }
    if (cell.glow < 0 || cell.glow > 1) {
      out.push({ rule: 'masque d’émission', detail: `${cell.name} : glow ${cell.glow} hors de [0,1]` });
    }
    if (seen.has(cell.name)) {
      out.push({ rule: 'atlas cohérent', detail: `cellule « ${cell.name} » en double` });
    }
    seen.add(cell.name);
  }

  // 7. L'atlas doit tenir dans sa grille : une cellule de trop déborderait sur
  //    la voisine, et toutes les UV du catalogue seraient décalées d'un cran —
  //    une flotte entière repeinte, sans une seule erreur de compilation.
  if (ATLAS_CELLS.length > ATLAS_SIDE * ATLAS_SIDE) {
    out.push({
      rule: 'atlas cohérent',
      detail: `${ATLAS_CELLS.length} cellules pour une grille ${ATLAS_SIDE}×${ATLAS_SIDE}`,
    });
  }

  // 8. Le décor reste sous son plafond de luminance. Un décor plus clair
  //    concurrence les projectiles, et le joueur perd la hiérarchie de lecture
  //    sans jamais pouvoir dire ce qui a changé.
  for (const cell of ATLAS_CELLS) {
    if (!DECOR_CELLS.includes(cell.name)) continue;
    const l = luminance(cell.hex);
    if (l > DECOR_MAX_LUMINANCE) {
      out.push({
        rule: 'plafond de luminance du décor',
        detail: `${cell.name} : ${(l * 100).toFixed(0)} % (max ${(DECOR_MAX_LUMINANCE * 100).toFixed(0)} %)`,
      });
    }
  }

  return out;
}

/** Luminance maximale autorisée dans le terrain pour le décor (PLAN.md §4.5).
 *  Un décor plus clair concurrence les projectiles, et le joueur perd la
 *  hiérarchie de lecture sans savoir pourquoi. */
export const DECOR_MAX_LUMINANCE = 0.45;
